/**
 * Ticket Type Icon Component
 * Displays an appropriate icon for each ticket type
 */

import { Box, Tooltip } from '@mui/material'
import {
  BugReport,
  Lightbulb,
  HelpOutline,
  Build,
  Person,
  Flag,
  MoreHoriz,
  Report,
  ContentCopy,
  GppMaybe,
  Copyright,
  Home,
  LocationOff,
  Block,
  HealthAndSafety,
} from '@mui/icons-material'
import { TicketType, TicketTypeLabels } from '@api/types/tickets'

interface TicketTypeIconProps {
  type: TicketType
  size?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  color?: string
}

const typeIcons: Record<TicketType, React.ReactNode> = {
  [TicketType.ContentInappropriate]: <Flag />,
  [TicketType.ContentSpam]: <Report />,
  [TicketType.ContentDuplicate]: <ContentCopy />,
  [TicketType.ContentVandalism]: <GppMaybe />,
  [TicketType.ContentCopyright]: <Copyright />,
  [TicketType.LocationPrivateProperty]: <Home />,
  [TicketType.LocationMissing]: <LocationOff />,
  [TicketType.LocationInaccessible]: <Block />,
  [TicketType.LocationSafety]: <HealthAndSafety />,
  [TicketType.Bug]: <BugReport />,
  [TicketType.FeatureRequest]: <Lightbulb />,
  [TicketType.Question]: <HelpOutline />,
  [TicketType.AccountIssue]: <Person />,
  [TicketType.TechnicalSupport]: <Build />,
  [TicketType.Other]: <MoreHoriz />,
}

const typeColors: Record<TicketType, string> = {
  [TicketType.ContentInappropriate]: '#e91e63',
  [TicketType.ContentSpam]: '#d32f2f',
  [TicketType.ContentDuplicate]: '#9e9e9e',
  [TicketType.ContentVandalism]: '#c62828',
  [TicketType.ContentCopyright]: '#6a1b9a',
  [TicketType.LocationPrivateProperty]: '#795548',
  [TicketType.LocationMissing]: '#607d8b',
  [TicketType.LocationInaccessible]: '#ff5722',
  [TicketType.LocationSafety]: '#d32f2f',
  [TicketType.Bug]: '#f44336',
  [TicketType.FeatureRequest]: '#ffeb3b',
  [TicketType.Question]: '#2196f3',
  [TicketType.AccountIssue]: '#9c27b0',
  [TicketType.TechnicalSupport]: '#ff9800',
  [TicketType.Other]: '#757575',
}

const sizeMap = {
  small: 18,
  medium: 24,
  large: 32,
}

export function TicketTypeIcon({ type, size = 'medium', showLabel = false, color }: TicketTypeIconProps) {
  const label = TicketTypeLabels[type]
  const iconColor = color || typeColors[type]
  const iconSize = sizeMap[size]

  const icon = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: showLabel ? 1 : 0,
        color: iconColor,
        '& svg': {
          fontSize: iconSize,
        },
      }}
      aria-label={`Type: ${label}`}
    >
      {typeIcons[type]}
      {showLabel && <span>{label}</span>}
    </Box>
  )

  if (showLabel) {
    return icon
  }

  return (
    <Tooltip title={label} arrow>
      {icon}
    </Tooltip>
  )
}
